import { Add, AppRegistration, SearchOutlined } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  TablePagination,
  TextField,
  Typography
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import { ChipStatusSelect } from 'components/chips'
import { useAuth, useLayout } from 'context'
import * as controllers from 'controllers'
import { PersonDto, StatusCount } from 'dtos'
import { useQuery } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { PeopleParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getStatusCount, useDebounce } from 'utils'
import PersonHeroCard from './PersonHeroCard'
import PersonAddEditDialog from './id/PersonAddEditDialog'

const sessionStorageKey = 'people:parameters'

export default function People() {
  const { permissions } = useAuth()
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const defaultParameters: PeopleParameters = {
    page: 0,
    pageSize: 10,
    search: query.get('search') ?? '',
    statuses: ['isActive'],
    orderBy: 'name',
    order: 'asc'
  }

  const [isCreatingPerson, setIsCreatingPerson] = useState<boolean>(false)
  const [isGettingPeople, setIsGettingPeople] = useState<boolean>(true)
  const [parameters, setParameters] = useState<PeopleParameters>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultParameters
  )
  const [people, setPeople] = useState<PersonDto[]>([])
  const [person, setPerson] = useState<PersonDto>(new PersonDto())
  const [personAddEditDialogOpen, setPersonAddEditDialogOpen] = useState<boolean>(false)
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getPeople = useDebounce((parameters: PeopleParameters) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(parameters))

    setIsGettingPeople(true)
    controllers
      .getPeople(parameters)
      .then(response => {
        setPeople(response.value)
        setStatusCounts(response.statusCounts ?? [])
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingPeople(false)
      })
  }, 300)

  useEffect(() => {
    getPeople(parameters)
  }, [getPeople, parameters])

  useEffect(() => {
    layout.setTitle('People')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingPeople)
  }, [layout, isGettingPeople])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <PersonAddEditDialog
          initialValues={person}
          loading={isCreatingPerson}
          onClose={() => {
            setPersonAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsCreatingPerson(true)
            controllers
              .createPerson(values)
              .then(() => {
                enqueueSnackbar('Person Saved Successfully!', { variant: 'success' })
                getPeople(parameters)
                setPersonAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsCreatingPerson(false)
              })
          }}
          open={personAddEditDialogOpen}
        />
      )}

      <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography fontWeight='bold' variant='h5'>
                People ({totalCount!})
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm={true} container spacing={1}>
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      data-search
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <SearchOutlined fontSize='small' />{' '}
                          </InputAdornment>
                        )
                      }}
                      label='Search'
                      onChange={e => {
                        setParameters({ ...parameters, search: e.target.value ?? undefined })
                      }}
                      size='small'
                      sx={{ minWidth: '200px', maxWidth: '200px' }}
                      type='search'
                      value={parameters.search || ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <ChipStatusSelect
                      label='Statuses'
                      onChange={value => {
                        setParameters({
                          ...parameters,
                          statuses: value
                        })
                      }}
                      options={[
                        {
                          label: `Active (${getStatusCount(statusCounts, 'isActive')})`,
                          color: 'green',
                          value: 'isActive'
                        },
                        {
                          label: `Inactive (${getStatusCount(statusCounts, 'isInactive')})`,
                          color: 'gray',
                          value: 'isInactive'
                        },
                        {
                          label: `Driver (${getStatusCount(statusCounts, 'isDriver')})`,
                          color: 'yellow',
                          value: 'isDriver'
                        },
                        {
                          label: `Declarant (${getStatusCount(statusCounts, 'isDeclarant')})`,
                          color: 'orange',
                          value: 'isDeclarant'
                        },
                        {
                          label: `User (${getStatusCount(statusCounts, 'isUser')})`,
                          color: 'purple',
                          value: 'isUser'
                        },
                        {
                          label: `Employee (${getStatusCount(statusCounts, 'isEmployee')})`,
                          color: 'blue',
                          value: 'isEmployee'
                        }
                      ]}
                      value={parameters.statuses ?? []}
                    />
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='secondary'
                      disabled={Boolean(JSON.stringify(parameters) === JSON.stringify(defaultParameters))}
                      onClick={() => {
                        setParameters(defaultParameters)
                      }}
                      variant='text'
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                    <Button
                      endIcon={<Add />}
                      onClick={() => {
                        setPerson(new PersonDto())
                        setPersonAddEditDialogOpen(true)
                      }}
                      variant='text'
                    >
                      ADD NEW
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {people.map(person => (
          <Grid item xs={12} key={person.personGuid}>
            <PersonHeroCard greenText={false} person={person}>
              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<AppRegistration />}
                  onClick={() => {
                    navigate(`/people/${person.personGuid}`)
                  }}
                  variant='text'
                >
                  DETAILS
                </Button>
              </Grid>
            </PersonHeroCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <ExplanationAccordion>
            The cards are sorted by First Name, Last Name, and then Company Name ascending. || Search searches all fields on the
            card. || The Statuses multi-select filters the list by the selected status(es). || The total number of people for a
            status is shown next to the status' name in parenthesis. This helps prevent users from adding a person that already
            exists.
          </ExplanationAccordion>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => {
              setParameters({
                ...parameters,
                page
              })
            }}
            onRowsPerPageChange={e => {
              setParameters({
                ...parameters,
                page: 0,
                pageSize: e.target.value as unknown as number
              })
            }}
            page={parameters.page}
            rowsPerPage={parameters.pageSize}
            labelRowsPerPage={
              <Grid container spacing={1}>
                <Grid item>Order By:</Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, orderBy: e.target.value })
                    }}
                    select
                    size='small'
                    value={parameters.orderBy}
                    variant='standard'
                  >
                    <MenuItem value='name'>Name</MenuItem>

                    <MenuItem value='company'>Company</MenuItem>

                    <MenuItem value='title'>Title</MenuItem>

                    <MenuItem value='email'>Email</MenuItem>

                    <MenuItem value='mobilePhone'>Mobile Phone</MenuItem>

                    <MenuItem value='officePhone'>Office Phone</MenuItem>

                    <MenuItem value='notes'>Notes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                    }}
                    select
                    size='small'
                    value={parameters.order}
                    variant='standard'
                  >
                    <MenuItem value='asc'>A-Z</MenuItem>

                    <MenuItem value='desc'>Z-A</MenuItem>
                  </TextField>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>Cards Per Page:</Grid>
              </Grid>
            }
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
